import Trim from 'analyses/alignment/Trim';
import {AlignmentLocation} from 'constants/AlignmentConstants';
import {AlignmentMapState} from 'molbio/maps/MapStates';
import SequenceAlignmentTabState from 'molbio/tabstates/SequenceAlignmentTabState';

interface PolymerInfo {
  residues: string;
  trimStart: number;
  trimEnd: number;
}

/**
 * Function to extract data from SequenceAlignmentTabState that will let us search for mismatches easily.
 * @param tabState
 * @return {
 *   masterPolymer: the residues of the master alignment row
 *   otherPolymers: an array of the residues of all the other alignment rows.
 *      Assumed to be from a GapSequenceWrapper, so all the same length
 *   rangeStart/rangeEnd: the ranges we should be looking for mismatches in, determined by the trim on
 *      the master alignment row
 *   currentIndex: where the user is currently focused, determined by the start of the current selection
 * }
 */
function extractMismatchDataFromTabState(tabState: SequenceAlignmentTabState): {
  masterPolymer: PolymerInfo;
  otherPolymers: Array<PolymerInfo>;
  rangeStart: number;
  rangeEnd: number;
  currentIndex: number;
} {
  const masterMapState = tabState.getMapStateForMasterSequence();
  const masterGapSequenceWrapper = masterMapState.get('sequence');
  // Consensus masters don't have trims
  const masterTrim: Trim | undefined = masterGapSequenceWrapper.get('trim');
  const selection = masterGapSequenceWrapper.get('selection');
  const masterPolymerResidues = masterGapSequenceWrapper.bases().toUpperCase();
  const masterPolymer: PolymerInfo = {
    residues: masterPolymerResidues,
    trimStart: masterTrim ? masterTrim.start() : 0,
    trimEnd: masterTrim ? masterTrim.end() : masterPolymerResidues.length,
  };
  const otherPolymers: Array<PolymerInfo> = tabState
    .get('mapstates')
    .filter((mapState: AlignmentMapState) => !mapState.isMasterSequence())
    .map((mapState: AlignmentMapState) => {
      const sequence = mapState.get('sequence');
      return {
        residues: sequence.bases().toUpperCase(),
        trimStart: sequence.get('trim')!.start(),
        trimEnd: sequence.get('trim')!.end(),
      };
    });
  const currentIndex = selection.start();
  return {
    masterPolymer,
    otherPolymers,
    rangeStart: masterPolymer.trimStart,
    rangeEnd: masterPolymer.trimEnd,
    currentIndex,
  };
}

/**
 * Find the residue at the given index across all polymers in the alignment. If the index is not within the
 * trim for the given polymer, return null.
 */
function getResiduesAtPositionAcrossPolymers(
  polymers: Array<PolymerInfo>,
  index: number
): Array<string | null> {
  return polymers.map((polymer) => {
    if (polymer.trimStart <= index && index < polymer.trimEnd) {
      return polymer.residues[index];
    }
    return null;
  });
}

/**
 * Return the index of the first polymer that mismatches the master polymer at the index, if any mismatch.
 */
function getFirstMismatchingPolymerIndex(
  masterPolymer: PolymerInfo,
  otherPolymers: Array<PolymerInfo>,
  index: number
): number | null {
  const residues = getResiduesAtPositionAcrossPolymers(otherPolymers, index);
  for (const [i, residue] of residues.entries()) {
    if (residue == null) {
      continue;
    }
    if (residue !== masterPolymer.residues[index]) {
      return i;
    }
  }
  return null;
}

function getMismatchLocationAtIndex(
  masterPolymer: PolymerInfo,
  otherPolymers: Array<PolymerInfo>,
  index: number
): AlignmentLocation | null {
  const mismatchRowIndex = getFirstMismatchingPolymerIndex(masterPolymer, otherPolymers, index);
  if (mismatchRowIndex != null) {
    return {residueIndex: index, rowIndex: mismatchRowIndex};
  }
  return null;
}

/**
 * Return the coordinates of the next (toward the right) mismatch in the alignment, if one exists.
 */
export function getNextMismatch(tabState: SequenceAlignmentTabState): AlignmentLocation | null {
  const {masterPolymer, otherPolymers, rangeEnd, rangeStart, currentIndex} =
    extractMismatchDataFromTabState(tabState);
  for (let i = Math.max(currentIndex + 1, rangeStart); i < rangeEnd; i++) {
    const location = getMismatchLocationAtIndex(masterPolymer, otherPolymers, i);
    if (location != null) {
      return location;
    }
  }
  // No mismatches found ahead of current selection
  return null;
}

/**
 * Return the coordinates of the previous (toward the left) mismatch in the alignment, if one exists.
 */
export function getPreviousMismatch(tabState: SequenceAlignmentTabState): AlignmentLocation | null {
  const {masterPolymer, otherPolymers, rangeStart, rangeEnd, currentIndex} =
    extractMismatchDataFromTabState(tabState);
  for (let i = Math.min(currentIndex - 1, rangeEnd); i >= rangeStart; i--) {
    const location = getMismatchLocationAtIndex(masterPolymer, otherPolymers, i);
    if (location != null) {
      return location;
    }
  }
  // No mismatches found behind of current selection
  return null;
}
